import type {GuideLevelFilterInput, GuideLevelSortInput} from 'gql/types';
import {GuideLevelSortField, SkillPathStatus, SortOrder} from 'gql/types';
import type {SortValue} from 'client/TeacherDashboard/types';

import {dueWithinBounds} from '../dueWindow';
import type {DueWithinFilter, SkillPathSortValue, SkillPathStatusSegment} from '../types';

export const STATUS_ENUM: Record<SkillPathStatusSegment, SkillPathStatus> = {
  open: SkillPathStatus.Open,
  closed: SkillPathStatus.Closed,
  scheduled: SkillPathStatus.Scheduled
};

/** Everything except the status segment — shared by the visible list and the per-status counts. */
export interface SharedSkillPathFilters {
  search: string;
  /** Subject id, or `'all'`. */
  subject: string;
  dueWithin: DueWithinFilter;
  dueDateStart: string;
  dueDateEnd: string;
}

/**
 * `null` means All Classes, which the server reads as the teacher's active classes. An empty array
 * is NOT the same thing — it scopes to no classroom at all and returns nothing.
 */
export const toClassroomIds = (classroomId?: string): string[] | null =>
  classroomId ? [classroomId] : null;

/**
 * The date bounds are evaluated against the recipient groups holding `status`, and match on overlap:
 * a skill belongs in the window when any of its due dates for that status falls inside.
 */
export const buildEntryFilter = (
  {search, subject, dueWithin, dueDateStart, dueDateEnd}: SharedSkillPathFilters,
  segment: SkillPathStatusSegment
): GuideLevelFilterInput => {
  const status = STATUS_ENUM[segment];
  const filter: GuideLevelFilterInput = {skillPathStatus: {eq: status}};

  if (search) {
    // Server-side across name and code, per the spec — not a name-only match.
    filter.matchesSearch = {eq: true, input: {search}};
  }
  if (subject !== 'all') {
    filter.subjectId = {eq: subject};
  }

  const {start, end} = dueWithinBounds(dueWithin, dueDateStart, dueDateEnd);
  if (start) {
    filter.skillPathMaxDueDate = {greaterThanOrEqual: start.toISOString(), input: {status}};
  }
  if (end) {
    filter.skillPathMinDueDate = {lessThanOrEqual: end.toISOString(), input: {status}};
  }
  return filter;
};

/** Every option sends an explicit sort, so the direction toggle means something for all of them. */
export const buildEntrySort = (
  {field, order}: SortValue<SkillPathSortValue>,
  segment: SkillPathStatusSegment
): GuideLevelSortInput[] => {
  const sortOrder = order === 'desc' ? SortOrder.Desc : SortOrder.Asc;
  const status = STATUS_ENUM[segment];

  switch (field) {
    case 'recentlyEdited':
      return [
        {field: GuideLevelSortField.SkillPathLastEditedAt, order: sortOrder},
        {field: GuideLevelSortField.Name, order: SortOrder.Asc}
      ];
    case 'name':
      return [{field: GuideLevelSortField.Name, order: sortOrder}];
    case 'startDate':
      return [
        {
          field: GuideLevelSortField.SkillPathMinStartDate,
          order: sortOrder,
          skillPathMinStartDateInput: {status}
        },
        {field: GuideLevelSortField.Name, order: SortOrder.Asc}
      ];
    case 'dueDate':
    default:
      return [
        {
          field: GuideLevelSortField.SkillPathMinDueDate,
          order: sortOrder,
          skillPathMinDueDateInput: {status}
        },
        {field: GuideLevelSortField.Name, order: SortOrder.Asc}
      ];
  }
};
